/**
 * Connect's review of clients' email templates — staff only, and internal.
 *
 * Every version of a client's template is analysed against the review rules
 * and decided by them; an employee then confirms or overturns the decision
 * here, as a second safeguard, and their word is final. The client is told
 * none of this: only that its template is being checked, approved, or needs
 * changes, in the words the rules give it. Nothing on these screens may be
 * copied to a client as it stands.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DetailField,
  DetailGrid,
  ErrorState,
  LoadingState,
  PageHeader,
  ScrollArea,
  ServerTable,
  Textarea,
  api,
  createServerFetcher,
  formatDateTime,
  formatNumber,
  toast,
  useCan,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { refusalText } from './api-refusal';
import { EMAIL_ENDPOINT, emailKeys, type ReviewDetail, type ReviewFinding, type ReviewRow } from './email';

export const STATUS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: 'Waiting', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
};
const ANALYSIS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: 'Queued', tone: 'neutral' },
  running: { label: 'Analysing', tone: 'info' },
  done: { label: 'Analysed', tone: 'success' },
  failed: { label: 'Could not analyse', tone: 'danger' },
  skipped: { label: 'Analysis off', tone: 'neutral' },
};
const RESULT: Record<string, { label: string; tone: Tone }> = {
  pass: { label: 'Pass', tone: 'success' },
  fail: { label: 'Fail', tone: 'danger' },
  unclear: { label: 'Unclear', tone: 'warning' },
};

function chip(map: Record<string, { label: string; tone: Tone }>, value: string | null | undefined) {
  if (!value) return <span className="text-xs text-faint">—</span>;
  const found = map[value] ?? { label: value, tone: 'neutral' as Tone };
  return <Badge tone={found.tone}>{found.label}</Badge>;
}

// ── the queue ───────────────────────────────────────────────
export function EmailReviewQueue() {
  const navigate = useNavigate();
  const fetchRows = useMemo(() => createServerFetcher<ReviewRow>(`${EMAIL_ENDPOINT}/reviews`), []);
  const columns = useMemo<ServerColumn<ReviewRow>[]>(
    () => [
      { field: 'client_name', header: 'Client' },
      { field: 'template_name', header: 'Template' },
      { field: 'category', header: 'Kind', width: 130, render: (row) => row.category ?? '—' },
      { field: 'status', header: 'Status', width: 120, render: (row) => chip(STATUS, row.status) },
      {
        field: 'ai_decision',
        header: 'Analysis',
        width: 150,
        render: (row) =>
          row.ai_decision ? (
            <span className="inline-flex items-center gap-1.5">
              {chip(RESULT, row.ai_decision === 'approve' ? 'pass' : 'fail')}
              <span className="text-xs text-muted">risk {row.ai_risk ?? '—'}</span>
            </span>
          ) : (
            chip(ANALYSIS, row.ai_status)
          ),
      },
      {
        field: 'staff_decision',
        header: 'Checked by staff',
        width: 150,
        render: (row) => (row.staff_decision ? chip(STATUS, row.staff_decision) : <Badge tone="warning">To check</Badge>),
      },
      { field: 'created_at', header: 'Saved', width: 170, render: (row) => formatDateTime(row.created_at) },
    ],
    [],
  );
  return (
    <div className="space-y-3">
      <Alert tone="info">
        Internal. Each client template's newest version, as analysed against the review rules. An approved template can
        already be sent; check it, and overturn the decision if it is wrong. Clients are never told a template was
        reviewed — only that it is being checked, approved, or needs changes.
      </Alert>
      <ServerTable<ReviewRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={emailKeys.reviews}
        stateKey="messaging.email.reviews"
        searchPlaceholder="Search by client or template"
        onView={(row) => navigate(`/messaging/email/reviews/${row.public_id}`)}
        emptyTitle="No templates yet"
        emptyDescription="A client's email template appears here as soon as it is saved."
      />
    </div>
  );
}

// ── one review ──────────────────────────────────────────────
export function EmailReviewPage() {
  const { publicId = '' } = useParams();
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const found = useQuery({
    queryKey: emailKeys.review(publicId),
    queryFn: () => api.get<ReviewDetail>(`${EMAIL_ENDPOINT}/reviews/${publicId}`),
  });
  const [note, setNote] = useState('');
  const [reasons, setReasons] = useState<string[]>([]);
  const settle = (data: ReviewDetail) => {
    queryClient.setQueryData(emailKeys.review(publicId), data);
    void queryClient.invalidateQueries({ queryKey: emailKeys.reviews });
  };
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api.put<ReviewDetail>(`${EMAIL_ENDPOINT}/reviews/${publicId}/decision`, { decision, note, reasons }),
    onSuccess: (data) => {
      settle(data);
      toast.success(data.status === 'approved' ? 'Approved' : 'Rejected', 'The client sees the change at once.');
    },
    onError: (error) => toast.error('Not saved', refusalText(error)),
  });
  const again = useMutation({
    mutationFn: () => api.post<ReviewDetail>(`${EMAIL_ENDPOINT}/reviews/${publicId}/analyse`, {}),
    onSuccess: (data) => {
      settle(data);
      toast.success('Queued', 'The template is analysed again within a minute.');
    },
    onError: (error) => toast.error('Not queued', refusalText(error)),
  });

  if (found.isLoading) return <LoadingState label="Loading the review" />;
  if (found.error || !found.data)
    return (
      <ErrorState
        message={found.error instanceof ApiError ? found.error.message : 'The review could not be loaded.'}
        onRetry={() => void found.refetch()}
      />
    );
  const data = found.data;
  const failed = data.ai.findings.filter((f) => f.result !== 'pass');
  const passed = data.ai.findings.filter((f) => f.result === 'pass');

  return (
    <div className="space-y-4">
      <PageHeader
        title={data.template.name}
        description={`${data.client.name} · ${data.template.category ?? 'marketing'} · saved ${formatDateTime(data.created_at)}`}
        actions={
          <Link to="/messaging/email?tab=reviews">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="size-3.5" /> All templates
            </Button>
          </Link>
        }
      />
      {data.current ? null : (
        <Alert tone="warning">The client has saved a newer version since. Decide on that one: it has a review of its own.</Alert>
      )}
      {data.template.deleted ? <Alert tone="info">The client has deleted this template.</Alert> : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Analysis"
              actions={
                <span className="inline-flex items-center gap-2">
                  {chip(ANALYSIS, data.ai.status)}
                  {data.ai.risk !== null ? (
                    <Badge tone={data.ai.risk >= 60 ? 'danger' : data.ai.risk >= 30 ? 'warning' : 'success'}>
                      Risk {data.ai.risk}
                    </Badge>
                  ) : null}
                </span>
              }
            />
            <CardBody className="space-y-3">
              {data.ai.error ? <Alert tone="warning">{data.ai.error}</Alert> : null}
              {data.ai.summary ? <p className="text-sm text-body">{data.ai.summary}</p> : null}
              {failed.length ? (
                <ul className="space-y-2">
                  {failed.map((finding) => (
                    <Finding key={finding.rule} finding={finding} />
                  ))}
                </ul>
              ) : data.ai.status === 'done' ? (
                <p className="text-sm text-muted">Every rule passed.</p>
              ) : null}
              {data.ai.recommendations.length ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-muted">Recommendations</p>
                  <ul className="list-disc pl-5 text-sm text-body">
                    {data.ai.recommendations.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {passed.length ? (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted">{passed.length} {passed.length === 1 ? 'rule' : 'rules'} passed</summary>
                  <ul className="mt-2 space-y-1">
                    {passed.map((finding) => (
                      <li key={finding.rule} className="text-xs text-muted">
                        {finding.title}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              <p className="text-xs text-faint">
                {data.ai.model ?? 'No model'} · {formatNumber(data.ai.tokens)} tokens · {data.ai.attempts} attempt
                {data.ai.attempts === 1 ? '' : 's'}
                {data.ai.reviewed_at ? ` · ${formatDateTime(data.ai.reviewed_at)}` : ''}
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="The template" description="As the client saved it, its HTML already made safe." />
            <CardBody className="space-y-3">
              <DetailGrid>
                <DetailField label="Subject">{data.template.subject || '—'}</DetailField>
                <DetailField label="Kind">{data.template.category ?? 'marketing'}</DetailField>
              </DetailGrid>
              {/* Sandboxed with no permissions: no script runs and no link opens here. */}
              <iframe
                title="The email as it reads"
                sandbox=""
                srcDoc={data.template.html}
                className="h-96 w-full rounded-md border border-line bg-white"
              />
              <details>
                <summary className="cursor-pointer text-sm text-muted">Plain text</summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-body">{data.template.text}</pre>
              </details>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="What Connect found" description="Read from the template itself, before the analysis." />
            <CardBody className="space-y-2">
              <p className="text-xs text-muted">
                {formatNumber(data.facts.images ?? 0)} images · {formatNumber(data.facts.html_bytes ?? 0)} bytes of HTML ·
                subject {Math.round((data.facts.subject_capitals_share ?? 0) * 100)}% capitals,{' '}
                {data.facts.subject_exclamations ?? 0} “!”
              </p>
              {(data.facts.links ?? []).length === 0 ? (
                <p className="text-sm text-muted">No links.</p>
              ) : (
                <ul className="space-y-1">
                  {(data.facts.links ?? []).map((link, index) => (
                    <li key={`${link.href}-${index}`} className="text-xs">
                      <span className="font-mono text-body">{link.href}</span>
                      {link.text ? <span className="text-muted"> — “{link.text}”</span> : null}{' '}
                      {link.own_domain ? <Badge tone="success">own domain</Badge> : null}
                      {link.shortener ? <Badge tone="danger">shortener</Badge> : null}
                      {link.bare_ip ? <Badge tone="danger">bare address</Badge> : null}
                      {link.download ? <Badge tone="danger">download</Badge> : null}
                      {link.link_text_mismatch ? <Badge tone="danger">text names another site</Badge> : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Decision" actions={chip(STATUS, data.status)} />
            <CardBody className="space-y-3">
              <div>
                <p className="text-2xs uppercase tracking-wide text-muted">What the client reads</p>
                <p className="text-sm text-heading">
                  {data.client_sees.sending_status === 'approved'
                    ? 'Approved'
                    : data.client_sees.sending_status === 'checking'
                      ? 'Being checked'
                      : 'Changes needed'}
                </p>
                {data.client_sees.sending_notes.map((line) => (
                  <p key={line} className="text-xs text-muted">
                    {line}
                  </p>
                ))}
              </div>
              {data.staff.decision ? (
                <Alert tone={data.staff.decision === 'approved' ? 'success' : 'danger'}>
                  {data.staff.decision === 'approved' ? 'Approved' : 'Rejected'} by {data.staff.by ?? 'a colleague'}
                  {data.staff.at ? ` on ${formatDateTime(data.staff.at)}` : ''}.
                  {data.staff.note ? ` ${data.staff.note}` : ''}
                </Alert>
              ) : (
                <Alert tone="warning">No member of staff has checked this yet.</Alert>
              )}
              {canEdit && data.current && !data.template.deleted ? (
                <>
                  <Textarea
                    label="Note for colleagues"
                    hint="Never shown to the client."
                    value={note}
                    maxLength={500}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <div>
                    <p className="text-sm font-medium text-heading">If rejecting, what the client is told</p>
                    <p className="text-xs text-muted">
                      Each rule's own words for clients. None chosen keeps the analysis's, or general words.
                    </p>
                    <ScrollArea className="mt-2 max-h-64">
                      <div className="space-y-1 pr-2">
                      {data.rules.map((rule) => (
                        <Checkbox
                          key={rule.key}
                          label={`${rule.title}${rule.severity === 'warn' ? ' (warning)' : ''}`}
                          checked={reasons.includes(rule.key)}
                          onChange={(event) =>
                            setReasons((was) =>
                              event.target.checked ? [...was, rule.key] : was.filter((key) => key !== rule.key),
                            )
                          }
                        />
                      ))}
                      </div>
                    </ScrollArea>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="ghost" loading={again.isPending} onClick={() => again.mutate()}>
                      Analyse again
                    </Button>
                    <Button variant="danger" loading={decide.isPending} onClick={() => decide.mutate('rejected')}>
                      Reject
                    </Button>
                    <Button loading={decide.isPending} onClick={() => decide.mutate('approved')}>
                      Approve
                    </Button>
                  </div>
                </>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Versions" />
            <CardBody>
              <ul className="space-y-1">
                {data.history.map((item) => (
                  <li key={item.public_id} className="flex items-center justify-between gap-2 text-xs">
                    <Link className="text-link" to={`/messaging/email/reviews/${item.public_id}`}>
                      {formatDateTime(item.created_at)}
                    </Link>
                    {chip(STATUS, item.status)}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Finding({ finding }: { finding: ReviewFinding }) {
  return (
    <li className="rounded-md border border-line p-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {chip(RESULT, finding.result)}
        <span className="text-sm font-medium text-heading">{finding.title}</span>
        <Badge tone={finding.severity === 'block' ? 'danger' : 'warning'}>
          {finding.severity === 'block' ? 'Blocking' : 'Warning'}
        </Badge>
      </div>
      {finding.evidence ? <p className="mt-1 text-xs text-body">“{finding.evidence}”</p> : null}
      {finding.recommendation ? <p className="mt-1 text-xs text-muted">{finding.recommendation}</p> : null}
    </li>
  );
}
